// Chakra imports
import {Badge, Box, Button, Flex, Text, useColorModeValue} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React from "react";

// Format any backend date value into a readable "01 Jan 2025", or "—".
const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {day: "2-digit", month: "short", year: "numeric"});
};

// Whole days from today until `value` (negative once expired), or null.
const daysUntil = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    const ms = date.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
    return Math.round(ms / (1000 * 60 * 60 * 24));
};

const PlatformSettings = ({title, school = {}}) => {
    // Chakra color mode
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");

    // Accept a few likely field names so this works against the current backend
    // shape without a schema change.
    const enrolledDate = school.enrolledDate || school.createdDate || school.startDate;
    const expiryDate = school.expiryDate || school.expireDate || school.expiredDate;

    const remaining = daysUntil(expiryDate);
    const isExpired = remaining !== null && remaining < 0;
    const isExpiring = remaining !== null && remaining >= 0 && remaining <= 30;

    const remainingLabel =
        remaining === null ? "—" : isExpired ? "Expired" : `${remaining} day${remaining === 1 ? "" : "s"}`;
    const remainingScheme = isExpired ? "red" : isExpiring ? "orange" : "green";

    return (
        <Card p="16px">
            <CardHeader p="12px 5px" mb="12px">
                <Text fontSize="lg" color={textColor} fontWeight="bold">
                    {title}
                </Text>
            </CardHeader>
            <CardBody px="5px">
                <Flex direction="column" w="100%">
                    <Box>
                        <Text fontSize="md" fontWeight="600" color={textColor} mb="12px">
                            Subscription
                        </Text>

                        <Flex align="center" justify="space-between" mb="12px">
                            <Text fontSize="sm" color={mutedColor} fontWeight="400">
                                Enrolled Date
                            </Text>
                            <Text fontSize="sm" color={textColor} fontWeight="600">
                                {formatDate(enrolledDate)}
                            </Text>
                        </Flex>

                        <Flex align="center" justify="space-between" mb="12px">
                            <Text fontSize="sm" color={mutedColor} fontWeight="400">
                                Expiry Date
                            </Text>
                            <Text fontSize="sm" color={textColor} fontWeight="600">
                                {formatDate(expiryDate)}
                            </Text>
                        </Flex>

                        <Flex align="center" justify="space-between" mb="20px">
                            <Text fontSize="sm" color={mutedColor} fontWeight="400">
                                Remaining
                            </Text>
                            <Badge
                                colorScheme={remainingScheme}
                                variant="subtle"
                                borderRadius="full"
                                px="10px"
                                py="2px"
                                textTransform="none"
                                fontSize="12px"
                            >
                                {remainingLabel}
                            </Badge>
                        </Flex>

                        <Flex gap="12px">
                            <Button colorScheme="teal" variant="solid" size="sm" flex="1">
                                Pay
                            </Button>
                            <Button colorScheme="blue" variant="outline" size="sm" flex="1">
                                Extend Plan
                            </Button>
                        </Flex>
                    </Box>
                </Flex>
            </CardBody>
        </Card>
    );
};

export default PlatformSettings;
